"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.ComputeRefManager = void 0;
const index_1 = require("../index");
const model_1 = require("../model");
const store_1 = require("../exports/store");
const types_1 = require("../types");
// Reference management for computed fields
class ComputeRefManager {
    constructor(refMap, reRefMap) {
        this.refMap = refMap || new Map();
        this.reRefMap = reRefMap || new Map();
    }
    addReRef(key, value) {
        this.reRefMap.set(key, value);
    }
    addRef(key, value) {
        // Simulate adding a new reference relationship
        const ref = this.refMap.get(key);
        if (ref) {
            ref.add(value);
        }
        else {
            this.refMap.set(key, new Set([value]));
        }
    }
    /**
     * Calculate the datasheetId collection of the forward or reverse dependence of the dimensional table dependence
     * @param dstId
     * @param fieldMap
     * @param refMap
     */
    getRefDstIds(dstId, fieldMap, refMap) {
        const fieldIds = Object.keys(fieldMap);
        const allKeys = fieldIds.map(fid => `${dstId}-${fid}`);
        const allDependenceKeys = new Set();
        const collectDependenceFieldKeys = (refKey) => {
            var _a;
            (_a = refMap.get(refKey)) === null || _a === void 0 ? void 0 : _a.forEach((key) => {
                if (!allDependenceKeys.has(key)) {
                    allDependenceKeys.add(key);
                    collectDependenceFieldKeys(key);
                }
            });
        };
        const hasErrorKeys = [];
        allKeys.forEach(key => {
            try {
                collectDependenceFieldKeys(key);
            }
            catch (error) {
                hasErrorKeys.push(key);
            }
        });
        const dstSet = new Set(Array.from(allDependenceKeys).map(key => key.split('-')[0]));
        return Array.from(dstSet);
    }
    // Check if there is a circular reference, if it exists, it will not pass
    checkRef(key, _visitedNode) {
        const visitedNode = _visitedNode || new Set();
        if (visitedNode.has(key)) {
            return false;
        }
        const ref = this.reRefMap.get(key);
        visitedNode.add(key);
        if (ref) {
            return Array.from(ref).every(key => this.checkRef(key, new Set(visitedNode)));
        }
        return true;
    }
    /**
     * TODO: Precisely clean up references.
     * 1. When a calculated field becomes a non-calculated field (deleted or field converted),
     * it is necessary to clean up the side of reRefMap as the key and the side of refMap as the value.
     * 2. Calculated fields are converted into other calculated fields, and formula lookup is converted to each other.
     * Cross-table non-cross-table dependent transformation
     * 3. The calculated field is not converted, but a dependency change has occurred.
     * For example, the formula originally depended on two fields A and B,
     * and later only depended on the A field, then the dependence on B should be removed.
     */
    cleanFieldRef(fieldId, datasheetId) {
        const key = `${datasheetId}-${fieldId}`;
        const thisFieldDeps = this.reRefMap.get(key);
        if (thisFieldDeps) {
            thisFieldDeps.forEach(item => {
                const thisFieldDep = this.refMap.get(item);
                thisFieldDep === null || thisFieldDep === void 0 ? void 0 : thisFieldDep.delete(key);
                if ((thisFieldDep === null || thisFieldDep === void 0 ? void 0 : thisFieldDep.size) === 0) {
                    this.refMap.delete(item);
                }
            });
            this.reRefMap.delete(key);
        }
        console.log(this.reRefMap, this.refMap);
    }
    /**
     * Clear dependencies and references.
     */
    clear() {
        this.refMap.clear();
        this.reRefMap.clear();
    }
    /*
     * Take the datasheet fieldMap as the entry to calculate the reference relationship of the datasheet,
     * and only calculate the reference relationship of one datasheet at a time
     * a - b - c three-table association,
     * When accessing table a, the server will also return meta and partial data of table b and c. In this case, computeRefMap is called three times.
     * @param fieldMap
     * @param datasheetId
     * @param state
     */
    computeRefMap(fieldMap, datasheetId, state, shouldSyncCache = true) {
        Object.values(fieldMap)
            .filter(field => model_1.Field.bindContext(field, state).isComputed || field.type === types_1.FieldType.Link)
            .forEach((field) => {
            switch (field.type) {
                case types_1.FieldType.Formula:
                    // formula may parse incorrectly
                    const formulaExpr = (0, index_1.parse)(field.property.expression, { field, fieldMap, state }, true);
                    if ('error' in formulaExpr) {
                        break;
                    }
                    const formulaRelatedFieldIds = new Set();
                    if (formulaExpr && formulaExpr.lexer.errors.length === 0) {
                        formulaExpr.lexer.matches.forEach(token => {
                            switch (token.type) {
                                case index_1.TokenType.Value:
                                    const fieldId = token.value.slice(1, -1);
                                    formulaRelatedFieldIds.add(fieldId);
                                    break;
                                case index_1.TokenType.PureValue:
                                    formulaRelatedFieldIds.add(token.value);
                                    break;
                            }
                        });
                    }
                    this.addReRef(`${datasheetId}-${field.id}`, new Set(Array.from(formulaRelatedFieldIds).map(fid => `${datasheetId}-${fid}`)));
                    formulaRelatedFieldIds.forEach(fid => {
                        const key = `${datasheetId}-${fid}`;
                        this.addRef(key, `${datasheetId}-${field.id}`);
                    });
                    break;
                // The lookup field depends on the link field of the current table and the looked field of the link table
                case types_1.FieldType.LookUp:
                    const keys = new model_1.LookUpField(field, state).getCurrentDatasheetRelatedFieldKeys(datasheetId);
                    this.addReRef(`${datasheetId}-${field.id}`, new Set(keys));
                    keys.forEach(key => {
                        this.addRef(key, `${datasheetId}-${field.id}`);
                    });
                    break;
                // The link field depends on the first field of the foreign key table
                case types_1.FieldType.Link:
                    const linkDstId = field.property.foreignDatasheetId;
                    const linkSnapshot = store_1.Selectors.getSnapshot(state, linkDstId);
                    if (linkSnapshot) {
                        const linkPrimaryField = store_1.Selectors.getDatasheetPrimaryField(linkSnapshot);
                        const key = `${linkDstId}-${linkPrimaryField.id}`;
                        this.addReRef(`${datasheetId}-${field.id}`, new Set([key]));
                        this.addRef(key, `${datasheetId}-${field.id}`);
                    }
                    break;
                default:
                    return;
            }
        });
        if (shouldSyncCache) {
            index_1.computeCache.set(index_1.COMPUTE_REF_MAP_CACHE_KEY, {
                refMap: this.refMap,
                reRefMap: this.reRefMap,
            });
        }
    }
    /**
     * Get all fields affected by a field
     */
    getAllEffectKeysByKey(key) {
        const effectedKeys = new Set();
        const collectEffectedFieldKeys = (refKey) => {
            var _a;
            (_a = this.refMap.get(refKey)) === null || _a === void 0 ? void 0 : _a.forEach((key) => {
                // First check if there is a circular reference.
                if (!effectedKeys.has(key)) {
                    effectedKeys.add(key);
                    collectEffectedFieldKeys(key);
                }
            });
        };
        try {
            collectEffectedFieldKeys(key);
        }
        catch (error) {
            return {
                hasError: true,
                effectedKeys
            };
        }
        return {
            hasError: false,
            effectedKeys
        };
    }
    /**
     * Check which table datasheetId collection a table depends on
     * @param dstId
     * @param fieldMap
     */
    getDependenceByDstIds(dstId, fieldMap) {
        return this.getRefDstIds(dstId, fieldMap, this.reRefMap);
    }
    /**
     * Check which tables a table depends on
     * @param dstId
     * @param fieldMap
     */
    getDependenceDstIds(dstId, fieldMap) {
        return this.getRefDstIds(dstId, fieldMap, this.refMap);
    }
}
exports.ComputeRefManager = ComputeRefManager;
//# sourceMappingURL=compute_reference_manager.js.map